import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';

// Types based on your API responses
export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  errors?: Array<{
    msg: string;
    param: string;
    location: string;
  }>;
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'admin' | 'student' | 'user';
  isActive: boolean;
  emailVerified: boolean;
  phone?: string;
  bio?: string;
  profileImage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Course {
  id: string;
  title: string;
  slug: string;
  description: string;
  price: number;
  level: string;
  category: string;
  isPublished: boolean;
  isActive: boolean;
  enrollmentCount: number;
  createdAt: string;
  lessons?: Lesson[];
  lessonCount?: number;
  thumbnail?: string;
  beforeImage?: string;
  afterImage?: string;
  certificateBackgroundUrl?: string;
}



export interface Lesson {
  id: string;
  courseId: string;
  title: string;
  description: string;
  content?: string;
  vimeoVideoId?: string;
  order: number;
  isPreview: boolean;
  isPublished: boolean;
  resources?: LessonResource[];  
}

export interface LessonResource {
  id: string;          // uuid or any stable id
  title: string;       // "Slides PDF", "Source code", etc.
  url: string;         // Google Drive link
  type?: string;       // 'pdf' | 'zip' | 'image' | 'link' | etc. (optional)
  sizeLabel?: string;  // "2.3 MB", optional
}

export interface Workshop {
  id: string;
  title: string;
  slug?: string;
  description: string;
  shortDescription?: string;
  startDate: string;
  endDate: string;
  maxParticipants: number;
  currentParticipants: number;
  price: number;
  earlyBirdPrice?: number;
  earlyBirdDeadline?: string;
  isOnline: boolean;
  location?: string;
  address?: string;
  zoomLink?: string;
  zoomPassword?: string;
  isPublished: boolean;
  isActive?: boolean;
  isFeatured?: boolean;
  category: string;
  level: string;
  duration: number;
  image?: string;
  images?: string[];
  tags?: string[];
  requirements?: string;
  whatToExpect?: string;
  materials?: string[];
  registrationDeadline?: string;
  cancellationPolicy?: string;
  instructorNotes?: string;
  googleFormUrl?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Enrollment {
  id: string;
  userId: string;
  courseId: string;
  progress: number;
  completedLessons: string[];
  currentLessonId?: string;
  timeSpent: number;
  lastAccessedAt?: string;
  completedAt?: string;  // Add this if your backend returns it
  certificateIssued: boolean;
  certificateUrl?: string;
  paymentStatus: 'pending' | 'completed' | 'failed' | 'refunded';
  paymentId?: string;
  enrollmentSource: 'direct' | 'coupon' | 'gift' | 'free';
  notes?: string;
  enrolledAt: string;
  course?: Course;
  createdAt?: string;
  updatedAt?: string;
}

export interface Registration {
  id: string;
  userId: string;
  workshopId: string;
  amountPaid: number;
  paymentStatus: 'pending' | 'completed' | 'failed' | 'refunded' | 'cancelled';
  attendanceStatus: 'registered' | 'attended' | 'no_show' | 'cancelled';
  registeredAt: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  paymentToken?: string;
  workshop?: Workshop;
  user?: User;
  createdAt?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface AuthResponse {
  user: User;
  token: string;
}

export interface LivestreamSettings {
  isLive: boolean;
  chatEnabled: boolean;
  vimeoId: string | null;
  title: string;
  description: string;
  startTime: string | null;
  scheduledFor: string | null;
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiResponse>) => {
        if (error.response?.status === 401) {
          // Don't redirect for auth endpoints (login, register, etc.)
          const isAuthEndpoint = error.config?.url?.startsWith('/auth/');
          const isAlreadyOnLogin = window.location.pathname === '/login';

          if (!isAuthEndpoint && !isAlreadyOnLogin) {
            // Token expired or invalid - redirect to login
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth methods
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response: AxiosResponse<ApiResponse<AuthResponse>> = await this.api.post('/auth/login', credentials);
    return response.data.data!;
  }

  async forgotPassword(email: string): Promise<void> {
    await this.api.post('/auth/forgot-password', { email });
  }

  async resetPassword(token: string, password: string): Promise<void> {
    await this.api.post('/auth/reset-password', { token, password });
  }

  async verifyEmail(token: string): Promise<void> {
    await this.api.post('/auth/verify-email', { token });
  }

  async resendVerification(email: string): Promise<void> {
    await this.api.post('/auth/resend-verification', { email });
  }

  async registerOnly(userData: RegisterData): Promise<void> {
    await this.api.post('/auth/register', userData);
    // Don't return token - user needs to verify email first
  }

  // User methods
  async getProfile(): Promise<User> {
    const response: AxiosResponse<ApiResponse<{ user: User }>> = await this.api.get('/users/profile');
    return response.data.data!.user;
  }

  async updateProfile(userData: Partial<User>): Promise<User> {
    const response: AxiosResponse<ApiResponse<{ user: User }>> = await this.api.put('/users/profile', userData);
    return response.data.data!.user;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.api.put('/users/change-password', { currentPassword, newPassword });
  }

  async uploadAvatar(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('avatar', file);
    
    const response: AxiosResponse<ApiResponse<{ profileImage: string }>> = await this.api.post('/users/upload-avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data!.profileImage;
  }

  async getUserEnrollments(): Promise<Enrollment[]> {
    const response: AxiosResponse<ApiResponse<{ enrollments: Enrollment[] }>> = await this.api.get('/users/enrollments');
    return response.data.data!.enrollments;
  }

  async getUserRegistrations(): Promise<Registration[]> {
    const response: AxiosResponse<ApiResponse<{ registrations: Registration[] }>> = await this.api.get('/users/registrations');
    return response.data.data!.registrations;
  }

  // Course methods
  async getCourses(params?: {
    category?: string;
    level?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ courses: Course[]; pagination: any }> {
    const response: AxiosResponse<ApiResponse<{ courses: Course[]; pagination: any }>> = await this.api.get('/courses', { params });
    return response.data.data!;
  }

  async getCourse(id: string): Promise<Course> {
    const response: AxiosResponse<ApiResponse<{ course: Course }>> = await this.api.get(`/courses/${id}`);
    return response.data.data!.course;
  }

  async enrollInCourse(courseId: string): Promise<Enrollment> {
    const response: AxiosResponse<ApiResponse<{ enrollment: Enrollment }>> = await this.api.post(`/courses/${courseId}/enroll`);
    return response.data.data!.enrollment;
  }

  // Lesson methods
async getLesson(courseId: string, lessonId: string): Promise<Lesson> {
  const response: AxiosResponse<ApiResponse<{ lesson: Lesson }>> = await this.api.get(`/courses/${courseId}/lessons/${lessonId}`);
  return response.data.data!.lesson;
}

async markLessonComplete(courseId: string, lessonId: string): Promise<{
  progress: number;
  completedLessons: string[];
  completedAt?: string;
}> {
  const response: AxiosResponse<ApiResponse<{
    progress: number;
    completedLessons: string[];
    completedAt?: string;
  }>> = await this.api.post(`/courses/${courseId}/lessons/${lessonId}/complete`);
  return response.data.data!;
}

async markLessonIncomplete(courseId: string, lessonId: string): Promise<{
  progress: number;
  completedLessons: string[];
}> {
  const response: AxiosResponse<ApiResponse<{
    progress: number;
    completedLessons: string[];
  }>> = await this.api.delete(`/courses/${courseId}/lessons/${lessonId}/complete`);
  return response.data.data!;
}

async updateLessonProgress(enrollmentId: string, lessonId: string, progressPercent: number): Promise<void> {
  await this.api.post(`/enrollments/${enrollmentId}/lessons/${lessonId}/progress`, { progress: progressPercent });
}

  // Admin Course methods
  async getAdminCourses(params?: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ courses: Course[]; pagination: any }> {
    const response: AxiosResponse<ApiResponse<{ courses: Course[]; pagination: any }>> = await this.api.get('/courses/admin/all', { params });
    return response.data.data!;
  }

  async getAdminCourse(id: string): Promise<{ course: Course }> {
    const response: AxiosResponse<ApiResponse<{ course: Course }>> = await this.api.get(`/courses/admin/${id}`);
    return response.data.data!;
  }

  async createCourse(data: Partial<Course>): Promise<Course> {
    const response: AxiosResponse<ApiResponse<{ course: Course }>> = await this.api.post('/courses', data);
    return response.data.data!.course;
  }

  async updateCourse(id: string, data: Partial<Course>): Promise<Course> {
    const response: AxiosResponse<ApiResponse<{ course: Course }>> = await this.api.put(`/courses/${id}`, data);
    return response.data.data!.course;
  }

  async deleteCourse(id: string): Promise<void> {
    await this.api.delete(`/courses/${id}`);
  }

  // Admin Lesson methods
  async createLesson(courseId: string, data: Partial<Lesson>): Promise<Lesson> {
    const response: AxiosResponse<ApiResponse<{ lesson: Lesson }>> = await this.api.post(`/courses/${courseId}/lessons`, data);
    return response.data.data!.lesson;
  }

  async updateLesson(courseId: string, lessonId: string, data: Partial<Lesson>): Promise<Lesson> {
    const response: AxiosResponse<ApiResponse<{ lesson: Lesson }>> = await this.api.put(`/courses/${courseId}/lessons/${lessonId}`, data);
    return response.data.data!.lesson;
  }

  async deleteLesson(courseId: string, lessonId: string): Promise<void> {
    await this.api.delete(`/courses/${courseId}/lessons/${lessonId}`);
  }

  // Workshop methods
  async getWorkshops(params?: {
    category?: string;
    level?: string;
    page?: number;
    limit?: number;
    past?: boolean;
  }): Promise<{ workshops: Workshop[]; pagination: any }> {
    const response: AxiosResponse<ApiResponse<{ workshops: Workshop[]; pagination: any }>> = await this.api.get('/workshops', { params });
    return response.data.data!;
  }

  async getWorkshop(id: string): Promise<Workshop> {
    const response: AxiosResponse<ApiResponse<{ workshop: Workshop }>> = await this.api.get(`/workshops/${id}`);
    return response.data.data!.workshop;
  }

  async registerForWorkshop(workshopId: string, data?: {
    specialRequests?: string;
    dietaryRestrictions?: string;
    emergencyContact?: {
      name: string;
      phone: string;
    };
  }): Promise<Registration> {
    const response: AxiosResponse<ApiResponse<{ registration: Registration }>> = await this.api.post(`/workshops/${workshopId}/register`, data);
    return response.data.data!.registration;
  }

  async cancelWorkshopRegistration(workshopId: string): Promise<void> {
    await this.api.delete(`/workshops/${workshopId}/register`);
  }

  // Admin Workshop methods
  async getAdminWorkshops(params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
  }): Promise<{ workshops: Workshop[]; pagination: any }> {
    const response: AxiosResponse<ApiResponse<{ workshops: Workshop[]; pagination: any }>> = await this.api.get('/admin/workshops', { params });
    return response.data.data!;
  }

  async getAdminWorkshop(id: string): Promise<{ workshop: Workshop; stats: any; registrations: Registration[] }> {
    const response: AxiosResponse<ApiResponse<{ workshop: Workshop; stats: any; registrations: Registration[] }>> = await this.api.get(`/admin/workshops/${id}`);
    return response.data.data!;
  }

  async createWorkshop(data: Partial<Workshop>): Promise<Workshop> {
    const response: AxiosResponse<ApiResponse<{ workshop: Workshop }>> = await this.api.post('/admin/workshops', data);
    return response.data.data!.workshop;
  }

  async updateWorkshop(id: string, data: Partial<Workshop>): Promise<Workshop> {
    const response: AxiosResponse<ApiResponse<{ workshop: Workshop }>> = await this.api.put(`/admin/workshops/${id}`, data);
    return response.data.data!.workshop;
  }

  async deleteWorkshop(id: string): Promise<void> {
    await this.api.delete(`/admin/workshops/${id}`);
  }

  async addWorkshopParticipant(workshopId: string, data: {
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
  }): Promise<Registration> {
    const response: AxiosResponse<ApiResponse<{ registration: Registration }>> = await this.api.post(`/admin/workshops/${workshopId}/participants`, data);
    return response.data.data!.registration;
  }

  async sendPaymentLink(workshopId: string, registrationId: string): Promise<void> {
    await this.api.post(`/admin/workshops/${workshopId}/participants/${registrationId}/send-payment`);
  }

  async markRegistrationAsPaid(workshopId: string, registrationId: string): Promise<Registration> {
    const response: AxiosResponse<ApiResponse<{ registration: Registration }>> = await this.api.put(`/admin/workshops/${workshopId}/participants/${registrationId}/mark-paid`);
    return response.data.data!.registration;
  }

  async removeParticipant(workshopId: string, registrationId: string): Promise<void> {
    await this.api.delete(`/admin/workshops/${workshopId}/participants/${registrationId}`);
  }

  async updateAttendance(workshopId: string, registrationId: string, status: 'attended' | 'no_show'): Promise<Registration> {
    const response: AxiosResponse<ApiResponse<{ registration: Registration }>> = await this.api.put(`/workshops/${workshopId}/attendees/${registrationId}/attendance`, { status });
    return response.data.data!.registration;
  }

  // Public token payment methods
  async getPaymentPageData(token: string): Promise<{ workshop: Workshop; registration: Registration }> {
    const response: AxiosResponse<ApiResponse<{ workshop: Workshop; registration: Registration }>> = await this.api.get(`/workshops/pay/${token}`);
    return response.data.data!;
  }

  async createTokenPaymentIntent(token: string): Promise<{ clientSecret: string; amount: number; currency: string }> {
    const response: AxiosResponse<ApiResponse<{ clientSecret: string; amount: number; currency: string }>> = await this.api.post(`/workshops/pay/${token}/create-intent`);
    return response.data.data!;
  }

  async confirmTokenPayment(token: string, paymentIntentId: string): Promise<void> {
    await this.api.post(`/workshops/pay/${token}/confirm`, { paymentIntentId });
  }

  // Payment methods
  async createPaymentIntent(type: 'course' | 'workshop', itemId: string, discountCode?: string): Promise<{
    clientSecret: string | null;
    amount: number;
    originalAmount?: number;
    discountAmount?: number;
    currency: string;
    freeWithDiscount?: boolean;
  }> {
    const response: AxiosResponse<ApiResponse<{
      clientSecret: string | null;
      amount: number;
      originalAmount?: number;
      discountAmount?: number;
      currency: string;
      freeWithDiscount?: boolean;
    }>> = await this.api.post('/payments/create-intent', { type, itemId, discountCode });
    return response.data.data!;
  }

  async confirmPayment(paymentIntentId: string): Promise<{ enrollment?: Enrollment; registration?: Registration }> {
    const response: AxiosResponse<ApiResponse<{ enrollment?: Enrollment; registration?: Registration }>> = await this.api.post('/payments/confirm', { paymentIntentId });
    return response.data.data!;
  }

  // Portfolio methods (public)
  async getPortfolio(params?: {
    category?: string;
    page?: number;
    limit?: number;
  }): Promise<{ images: any[]; pagination: any }> {
    const response: AxiosResponse<ApiResponse<{ images: any[]; pagination: any }>> = await this.api.get('/portfolio', { params });
    return response.data.data!;
  }

  async getFeaturedPortfolio(): Promise<{ images: any[] }> {
    const response: AxiosResponse<ApiResponse<{ images: any[] }>> = await this.api.get('/portfolio/featured');
    return response.data.data!;
  }

  async getPortfolioCategories(): Promise<{ categories: any[] }> {
    const response: AxiosResponse<ApiResponse<{ categories: any[] }>> = await this.api.get('/portfolio/categories');
    return response.data.data!;
  }

  // Newsletter
  async subscribeToNewsletter(email: string, name?: string): Promise<{ success: boolean; message: string }> {
    const response: AxiosResponse<{ success: boolean; message: string }> = await this.api.post('/newsletter/subscribe', { email, name });
    return response.data;
  }

  async getNewsletterCampaigns(): Promise<{ success: boolean; campaigns: NewsletterCampaign[] }> {
    const response: AxiosResponse<{ success: boolean; campaigns: NewsletterCampaign[] }> = await this.api.get('/newsletter/campaigns');
    return response.data;
  }

  async getNewsletterCampaign(id: string, email?: string): Promise<{ success: boolean; campaign: NewsletterCampaignDetail }> {
    const params = email ? { email } : {};
    const response: AxiosResponse<{ success: boolean; campaign: NewsletterCampaignDetail }> = await this.api.get(`/newsletter/campaigns/${id}`, { params });
    return response.data;
  }

  async verifyNewsletterSubscription(email: string): Promise<{ success: boolean; subscribed: boolean }> {
    const response: AxiosResponse<{ success: boolean; subscribed: boolean }> = await this.api.get(`/newsletter/verify/${encodeURIComponent(email)}`);
    return response.data;
  }

  // Admin methods
  async getAdminDashboard(params?: { startDate?: string; endDate?: string }): Promise<AdminDashboardStats> {
    const response: AxiosResponse<ApiResponse<AdminDashboardStats>> = await this.api.get('/admin/dashboard', { params });
    return response.data.data!;
  }

  async getRevenueAnalytics(params?: AdminAnalyticsParams): Promise<AdminRevenueAnalytics> {
    const response: AxiosResponse<ApiResponse<AdminRevenueAnalytics>> = await this.api.get('/admin/analytics/revenue', { params });
    return response.data.data!;
  }

  async getUserAnalytics(params?: AdminAnalyticsParams): Promise<AdminUserAnalytics> {
    const response: AxiosResponse<ApiResponse<AdminUserAnalytics>> = await this.api.get('/admin/analytics/users', { params });
    return response.data.data!;
  }

  async getEngagementAnalytics(params?: AdminAnalyticsParams): Promise<AdminEngagementAnalytics> {
    const response: AxiosResponse<ApiResponse<AdminEngagementAnalytics>> = await this.api.get('/admin/analytics/engagement', { params });
    return response.data.data!;
  }

  async getCompetitionAnalytics(params?: AdminAnalyticsParams): Promise<AdminCompetitionAnalytics> {
    const response: AxiosResponse<ApiResponse<AdminCompetitionAnalytics>> = await this.api.get('/admin/analytics/competitions', { params });
    return response.data.data!;
  }

  async getAdminUsers(params?: AdminUserListParams): Promise<{ users: User[]; pagination: any }> {
    const response: AxiosResponse<ApiResponse<{ users: User[]; pagination: any }>> = await this.api.get('/admin/users', { params });
    return response.data.data!;
  }

  async getAdminUserDetail(userId: string): Promise<AdminUserDetail> {
    const response: AxiosResponse<ApiResponse<AdminUserDetail>> = await this.api.get(`/admin/users/${userId}`);
    return response.data.data!;
  }

  async updateUserStatus(userId: string, isActive: boolean): Promise<{ user: User }> {
    const response: AxiosResponse<ApiResponse<{ user: User }>> = await this.api.put(`/admin/users/${userId}/status`, { isActive });
    return response.data.data!;
  }

  async adminEnrollUser(userId: string, courseId: string): Promise<void> {
    await this.api.post(`/admin/users/${userId}/enroll`, { courseId });
  }

  // Livestream methods
  async getLivestream(): Promise<LivestreamSettings> {
    const response: AxiosResponse<ApiResponse<{ livestream: LivestreamSettings }>> = await this.api.get('/livestream');
    return response.data.data!.livestream;
  }

  async updateLivestream(settings: Partial<LivestreamSettings>): Promise<LivestreamSettings> {
    const response: AxiosResponse<ApiResponse<{ livestream: LivestreamSettings }>> = await this.api.put('/livestream', settings);
    return response.data.data!.livestream;
  }

  // Certificate methods
  async downloadCertificate(enrollmentId: string): Promise<Blob> {
    const response = await this.api.get(`/certificates/${enrollmentId}`, {
      responseType: 'blob'
    });

    // Check if response is actually JSON error (blob with type application/json)
    const contentType = response.data.type || response.headers['content-type'];

    if (contentType && contentType.includes('application/json')) {
      const text = await response.data.text();
      try {
        const error = JSON.parse(text);
        throw new Error(error.message || 'Błąd generowania certyfikatu');
      } catch (parseError) {
        throw new Error('Błąd generowania certyfikatu');
      }
    }

    // Verify it's actually a PDF
    if (!contentType || !contentType.includes('application/pdf')) {
      console.error('Unexpected content type:', contentType);
      throw new Error('Otrzymano nieprawidłowy format pliku');
    }

    return response.data;
  }

  getCertificatePreviewUrl(enrollmentId: string): string {
    const baseURL = this.api.defaults.baseURL;
    const token = localStorage.getItem('token');
    return `${baseURL}/certificates/${enrollmentId}/preview?token=${token}`;
  }

  // Competition methods
  async getCompetitions(params?: {
    page?: number;
    limit?: number;
    past?: boolean;
  }): Promise<{ competitions: Competition[]; pagination: any }> {
    const response: AxiosResponse<ApiResponse<{ competitions: Competition[]; pagination: any }>> = await this.api.get('/competitions', { params });
    return response.data.data!;
  }

  async getCompetitionPageSettings(): Promise<CompetitionPageSettings> {
    const response: AxiosResponse<ApiResponse<{ settings: CompetitionPageSettings }>> = await this.api.get('/competitions/page-settings');
    return response.data.data!.settings;
  }

  async getCompetition(id: string): Promise<Competition> {
    const response: AxiosResponse<ApiResponse<{ competition: Competition }>> = await this.api.get(`/competitions/${id}`);
    return response.data.data!.competition;
  }

  async submitToCompetition(competitionId: string, data: {
    photos: File[];
    descriptions: string[];
    guestEmail?: string;
    guestFirstName?: string;
    guestLastName?: string;
  }): Promise<CompetitionSubmission> {
    const formData = new FormData();

    data.photos.forEach((photo) => {
      formData.append('photos', photo);
    });

    formData.append('descriptions', JSON.stringify(data.descriptions));

    if (data.guestEmail) formData.append('guestEmail', data.guestEmail);
    if (data.guestFirstName) formData.append('guestFirstName', data.guestFirstName);
    if (data.guestLastName) formData.append('guestLastName', data.guestLastName);

    const response: AxiosResponse<ApiResponse<{ submission: CompetitionSubmission }>> = await this.api.post(
      `/competitions/${competitionId}/submit`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data!.submission;
  }

  async createCompetitionPayment(submissionId: string, discountCode?: string): Promise<{
    clientSecret: string | null;
    amount: number;
    originalAmount?: number;
    discountAmount?: number;
    submissionId: string;
    accessToken?: string;
    freeWithDiscount?: boolean;
  }> {
    const response: AxiosResponse<ApiResponse<{
      clientSecret: string | null;
      amount: number;
      originalAmount?: number;
      discountAmount?: number;
      submissionId: string;
      accessToken?: string;
      freeWithDiscount?: boolean;
    }>> = await this.api.post(`/competitions/submissions/${submissionId}/create-payment`, { discountCode });
    return response.data.data!;
  }

  async confirmCompetitionPayment(submissionId: string, paymentIntentId: string): Promise<{
    submission: { id: string; status: string; googleDriveFolderUrl?: string; accessToken?: string };
  }> {
    const response: AxiosResponse<ApiResponse<{
      submission: { id: string; status: string; googleDriveFolderUrl?: string; accessToken?: string };
    }>> = await this.api.post(`/competitions/submissions/${submissionId}/confirm-payment`, { paymentIntentId });
    return response.data.data!;
  }

  async getCompetitionFeedback(accessToken: string): Promise<CompetitionSubmission> {
    const response: AxiosResponse<ApiResponse<{ submission: CompetitionSubmission }>> = await this.api.get(`/competitions/feedback/${accessToken}`);
    return response.data.data!.submission;
  }

  async getMyCompetitionSubmissions(): Promise<CompetitionSubmission[]> {
    const response: AxiosResponse<ApiResponse<{ submissions: CompetitionSubmission[] }>> = await this.api.get('/competitions/my-submissions');
    return response.data.data!.submissions;
  }

  async getCompetitionSubmission(submissionId: string): Promise<CompetitionSubmission> {
    const response: AxiosResponse<ApiResponse<{ submission: CompetitionSubmission }>> = await this.api.get(`/competitions/submissions/${submissionId}`);
    return response.data.data!.submission;
  }

  // Admin competition methods
  async getAdminCompetitions(params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
  }): Promise<{ competitions: Competition[]; pagination: any }> {
    const response: AxiosResponse<ApiResponse<{ competitions: Competition[]; pagination: any }>> = await this.api.get('/admin/competitions', { params });
    return response.data.data!;
  }

  async getAdminCompetitionPageSettings(): Promise<CompetitionPageSettings> {
    const response: AxiosResponse<ApiResponse<{ settings: CompetitionPageSettings }>> = await this.api.get('/admin/competitions/page-settings');
    return response.data.data!.settings;
  }

  async updateCompetitionPageSettings(settings: Partial<CompetitionPageSettings>): Promise<CompetitionPageSettings> {
    const response: AxiosResponse<ApiResponse<{ settings: CompetitionPageSettings }>> = await this.api.put('/admin/competitions/page-settings', settings);
    return response.data.data!.settings;
  }

  async getAdminCompetition(id: string): Promise<{ competition: Competition; stats: any }> {
    const response: AxiosResponse<ApiResponse<{ competition: Competition; stats: any }>> = await this.api.get(`/admin/competitions/${id}`);
    return response.data.data!;
  }

  async getAdminCompetitionDetails(id: string): Promise<{
    competition: Competition;
    stats: {
      totalSubmissions: number;
      paidSubmissions: number;
      pendingSubmissions: number;
      totalPhotos: number;
      ratedPhotos: number;
      feedbackSent: number;
      feedbackPending: number;
      totalRevenue: number;
      avgPhotosPerSubmission: number;
    };
    recentSubmissions: CompetitionSubmission[];
  }> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/admin/competitions/${id}/details`);
    return response.data.data!;
  }

  async createCompetition(data: Partial<Competition>): Promise<Competition> {
    const response: AxiosResponse<ApiResponse<{ competition: Competition }>> = await this.api.post('/admin/competitions', data);
    return response.data.data!.competition;
  }

  async updateCompetition(id: string, data: Partial<Competition>): Promise<Competition> {
    const response: AxiosResponse<ApiResponse<{ competition: Competition }>> = await this.api.put(`/admin/competitions/${id}`, data);
    return response.data.data!.competition;
  }

  async deleteCompetition(id: string): Promise<void> {
    await this.api.delete(`/admin/competitions/${id}`);
  }

  async getCompetitionSubmissions(competitionId: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
    hasFeedback?: boolean;
  }): Promise<{ submissions: CompetitionSubmission[]; pagination: any }> {
    const response: AxiosResponse<ApiResponse<{ submissions: CompetitionSubmission[]; pagination: any }>> = await this.api.get(`/admin/competitions/${competitionId}/submissions`, { params });
    return response.data.data!;
  }

  async getAdminSubmission(submissionId: string): Promise<CompetitionSubmission> {
    const response: AxiosResponse<ApiResponse<{ submission: CompetitionSubmission }>> = await this.api.get(`/admin/submissions/${submissionId}`);
    return response.data.data!.submission;
  }

  async addSubmissionFeedback(submissionId: string, data: {
    feedbackVideoUrl?: string;
    feedbackNotes?: string;
    sendNotification?: boolean;
  }): Promise<CompetitionSubmission> {
    const response: AxiosResponse<ApiResponse<{ submission: CompetitionSubmission }>> = await this.api.put(`/admin/submissions/${submissionId}/feedback`, data);
    return response.data.data!.submission;
  }

  // Admin photo grading methods
  async getCompetitionPhotos(competitionId: string, params?: {
    sortBy?: 'date' | 'rating' | 'unrated';
    filterRating?: 'all' | 'rated' | 'unrated';
  }): Promise<{
    competition: { id: string; title: string };
    photos: SubmissionPhoto[];
    stats: {
      totalPhotos: number;
      ratedPhotos: number;
      unratedPhotos: number;
      avgRating: string | null;
    };
  }> {
    const response: AxiosResponse<ApiResponse<{
      competition: { id: string; title: string };
      photos: SubmissionPhoto[];
      stats: {
        totalPhotos: number;
        ratedPhotos: number;
        unratedPhotos: number;
        avgRating: string | null;
      };
    }>> = await this.api.get(`/admin/competitions/${competitionId}/photos`, { params });
    return response.data.data!;
  }

  async gradePhoto(photoId: string, data: {
    adminNotes?: string;
    adminRating?: number | null;
  }): Promise<SubmissionPhoto> {
    const response: AxiosResponse<ApiResponse<{ photo: SubmissionPhoto }>> = await this.api.put(`/admin/photos/${photoId}/grade`, data);
    return response.data.data!.photo;
  }

  // Discount Code methods
  async validateDiscountCode(
    code: string,
    productType: 'course' | 'workshop' | 'competition',
    productId: string,
    amount: number,
    guestEmail?: string
  ): Promise<DiscountValidationResult> {
    const response: AxiosResponse<ApiResponse<DiscountValidationResult>> = await this.api.post('/discount-codes/validate', {
      code,
      productType,
      productId,
      amount,
      guestEmail
    });
    return response.data.data!;
  }

  // Admin Discount Code methods
  async getDiscountCodes(params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
  }): Promise<{ discountCodes: DiscountCode[]; pagination: any }> {
    const response: AxiosResponse<ApiResponse<{ discountCodes: DiscountCode[]; pagination: any }>> = await this.api.get('/discount-codes', { params });
    return response.data.data!;
  }

  async getDiscountCode(id: string): Promise<{
    discountCode: DiscountCode;
    usageStats: any[];
    recentUsages: DiscountCodeUsage[];
  }> {
    const response: AxiosResponse<ApiResponse<{
      discountCode: DiscountCode;
      usageStats: any[];
      recentUsages: DiscountCodeUsage[];
    }>> = await this.api.get(`/discount-codes/${id}`);
    return response.data.data!;
  }

  async createDiscountCode(data: Partial<DiscountCode>): Promise<DiscountCode> {
    const response: AxiosResponse<ApiResponse<{ discountCode: DiscountCode }>> = await this.api.post('/discount-codes', data);
    return response.data.data!.discountCode;
  }

  async updateDiscountCode(id: string, data: Partial<DiscountCode>): Promise<DiscountCode> {
    const response: AxiosResponse<ApiResponse<{ discountCode: DiscountCode }>> = await this.api.put(`/discount-codes/${id}`, data);
    return response.data.data!.discountCode;
  }

  async deleteDiscountCode(id: string): Promise<void> {
    await this.api.delete(`/discount-codes/${id}`);
  }

  // Email Campaign methods
  async getCampaignAudiences(): Promise<{ audiences: EmailCampaignAudience[] }> {
    const response: AxiosResponse<ApiResponse<{ audiences: EmailCampaignAudience[] }>> = await this.api.get('/admin/campaigns/audiences');
    return response.data.data!;
  }

  async previewCampaignRecipients(audienceType: string, audienceId?: string): Promise<{
    recipients: { email: string; firstName: string; lastName: string }[];
    count: number;
  }> {
    const response: AxiosResponse<ApiResponse<{
      recipients: { email: string; firstName: string; lastName: string }[];
      count: number;
    }>> = await this.api.post('/admin/campaigns/preview-recipients', { audienceType, audienceId });
    return response.data.data!;
  }

  async sendCampaign(data: {
    subject: string;
    body: string;
    htmlContent: string;
    audienceType: string;
    audienceId?: string;
    audienceLabel: string;
    customEmails?: string[];
  }): Promise<{ campaign: EmailCampaign }> {
    const response: AxiosResponse<ApiResponse<{ campaign: EmailCampaign }>> = await this.api.post('/admin/campaigns/send', data);
    return response.data.data!;
  }

  async getCampaigns(params?: { page?: number; limit?: number }): Promise<{
    campaigns: EmailCampaign[];
    pagination: { total: number; page: number; pages: number; limit: number };
  }> {
    const response: AxiosResponse<ApiResponse<{
      campaigns: EmailCampaign[];
      pagination: { total: number; page: number; pages: number; limit: number };
    }>> = await this.api.get('/admin/campaigns', { params });
    return response.data.data!;
  }

  async getCampaign(id: string): Promise<{ campaign: EmailCampaign }> {
    const response: AxiosResponse<ApiResponse<{ campaign: EmailCampaign }>> = await this.api.get(`/admin/campaigns/${id}`);
    return response.data.data!;
  }

  async refreshCampaignStatus(id: string): Promise<{ campaign: EmailCampaign }> {
    const response: AxiosResponse<ApiResponse<{ campaign: EmailCampaign }>> = await this.api.post(`/admin/campaigns/${id}/refresh-status`);
    return response.data.data!;
  }
}

// Competition types
export interface Competition {
  id: string;
  title: string;
  slug: string;
  description: string;
  shortDescription?: string;
  pricePerPhoto: number;
  minPhotos: number;
  maxPhotos: number;
  deadline?: string;
  thumbnailUrl?: string;
  rules?: string;
  googleDriveFolderId?: string;
  isActive: boolean;
  isPublished: boolean;
  isFeatured: boolean;
  isResolved: boolean;
  resultsContent?: string;
  resolvedAt?: string;
  submissionCount: number;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
  stats?: {
    totalSubmissions: number;
    paidSubmissions: number;
  };
}

export interface CompetitionPageSettings {
  announcement: {
    enabled: boolean;
    title: string;
    subtitle: string;
    liveDate: string;
    liveDateLabel: string;
    feedbackTitle: string;
    feedbackText: string;
  };
  feedbackLookup: {
    enabled: boolean;
  };
}

export interface CompetitionSubmission {
  id: string;
  competitionId: string;
  userId?: string;
  guestEmail?: string;
  guestFirstName?: string;
  guestLastName?: string;
  accessToken?: string;
  photoCount: number;
  totalAmount: number;
  paymentStatus: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded';
  paymentId?: string;
  paidAt?: string;
  googleDriveFolderId?: string;
  googleDriveFolderUrl?: string;
  feedbackVideoUrl?: string;
  feedbackNotes?: string;
  feedbackPostedAt?: string;
  feedbackEmailSent: boolean;
  status: 'draft' | 'submitted' | 'paid' | 'reviewed' | 'feedback_sent';
  competition?: Competition;
  user?: User;
  photos?: SubmissionPhoto[];
  createdAt: string;
  updatedAt: string;
}

export interface SubmissionPhoto {
  id: string;
  submissionId: string;
  order: number;
  originalFilename: string;
  description?: string;
  localPath?: string;
  googleDriveFileId?: string;
  googleDriveUrl?: string;
  thumbnailUrl?: string;
  fileSize?: number;
  mimeType?: string;
  uploadedAt?: string;
  adminNotes?: string;
  adminRating?: number;
  createdAt: string;
  updatedAt: string;
}

// Admin types
export interface AdminDashboardStats {
  overview: {
    totalUsers: number;
    newUsersInPeriod: number;
    activeUsers: number;
    totalCourses: number;
    totalWorkshops: number;
    totalCompetitions: number;
    activeCompetitions: number;
  };
  revenue: {
    total: number;
    courses: number;
    workshops: number;
    competitions: number;
    enrollmentsCount: number;
    registrationsCount: number;
    competitionSubmissionsCount: number;
    totalPhotosSubmitted: number;
  };
  revenueTrend: Array<{
    date: string;
    courses: number;
    workshops: number;
    competitions: number;
    total: number;
  }>;
  recentActivity: {
    enrollments: Array<Enrollment & { user: User; course: Course }>;
    registrations: Array<Registration & { user: User; workshop: Workshop }>;
    competitionSubmissions: Array<{
      id: string;
      guestEmail?: string;
      guestFirstName?: string;
      guestLastName?: string;
      photoCount: number;
      totalAmount: number;
      createdAt: string;
      user?: User;
      competition: { id: string; title: string; slug: string };
    }>;
  };
  period: {
    start: string;
    end: string;
  };
}

export interface AdminRevenueAnalytics {
  byCourse: Array<{
    courseId: string;
    courseTitle: string;
    price: number;
    enrollments: number;
    revenue: number;
  }>;
  byWorkshop: Array<{
    workshopId: string;
    workshopTitle: string;
    registrations: number;
    revenue: number;
  }>;
  byCompetition: Array<{
    competitionId: string;
    competitionTitle: string;
    pricePerPhoto: number;
    submissions: number;
    totalPhotos: number;
    revenue: number;
  }>;
  totals: {
    courses: number;
    workshops: number;
    competitions: number;
    total: number;
  };
  trends: {
    courses: Array<{ date: string; count: number }>;
    workshops: Array<{ date: string; revenue: number; count: number }>;
    competitions: Array<{ date: string; revenue: number; count: number; photos: number }>;
  };
  refunds: {
    courseRefunds: number;
    workshopRefunds: number;
  };
  period: { start: string; end: string };
}

export interface AdminUserAnalytics {
  growth: Array<{ date: string; count: number }>;
  roleDistribution: { [key: string]: number };
  activityStats: { active: number; inactive: number };
  verificationStats: { verified: number; unverified: number };
  engagement: { withEnrollments: number; withRegistrations: number };
  period: { start: string; end: string };
}

export interface AdminEngagementAnalytics {
  courseCompletions: Array<{
    courseId: string;
    courseTitle: string;
    avgProgress: number;
    totalEnrollments: number;
    completedCount: number;
    completionRate: string;
  }>;
  timeSpent: Array<{
    courseId: string;
    courseTitle: string;
    avgTimeSpent: number;
    totalTimeSpent: number;
  }>;
  popularCourses: Course[];
  recentlyActiveEnrollments: number;
  workshopAttendance: { [key: string]: number };
  period: { start: string; end: string };
}

export interface AdminCompetitionAnalytics {
  overview: {
    totalCompetitions: number;
    activeCompetitions: number;
    resolvedCompetitions: number;
    totalSubmissions: number;
    totalPhotos: number;
    totalRevenue: number;
    avgPhotosPerSubmission: string;
  };
  submissionBreakdown: {
    users: number;
    guests: number;
  };
  feedbackStatus: {
    sent: number;
    pending: number;
    completionRate: string;
  };
  byCompetition: Array<{
    id: string;
    title: string;
    isPublished: boolean;
    isResolved: boolean;
    isActive: boolean;
    deadline: string | null;
    submissions: number;
    totalPhotos: number;
    ratedPhotos: number;
    avgRating: string | null;
    feedbackSent: number;
    feedbackPending: number;
    uniqueUsers: number;
    guestSubmissions: number;
    revenue: number;
  }>;
  submissionsTrend: Array<{
    date: string;
    submissions: number;
    photos: number;
    revenue: number;
  }>;
  photoCountDistribution: Array<{
    photos: number;
    count: number;
  }>;
  period: { start: string; end: string };
}

export interface AdminUserDetail {
  user: User;
  stats: {
    totalSpent: number;
    totalSpentCourses: number;
    totalSpentWorkshops: number;
    enrollmentsCount: number;
    registrationsCount: number;
    completedCourses: number;
    avgProgress: string;
  };
  enrollments: Enrollment[];
  registrations: Registration[];
}

export interface AdminUserListParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  isActive?: string;
  sortBy?: string;
  sortOrder?: string;
}

export interface AdminAnalyticsParams {
  startDate?: string;
  endDate?: string;
  groupBy?: 'day' | 'week' | 'month';
}

// Newsletter types
export interface NewsletterCampaign {
  id: string;
  name: string;
  subject: string;
  sentAt: string;
  previewText: string;
  thumbnailUrl: string | null;
  stats: {
    sent: number;
    opens: number;
    clicks: number;
  };
}

export interface NewsletterCampaignDetail {
  id: string;
  name: string;
  subject: string;
  content: string;
  sentAt: string;
  previewText: string;
}

// Discount Code types
export interface DiscountCode {
  id: string;
  code: string;
  description?: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  maxDiscountAmount?: number;
  minimumPurchaseAmount?: number;
  applicableTo: ('course' | 'workshop' | 'competition')[];
  specificProductIds?: string[];
  usageLimit?: number;
  usageLimitPerUser: number;
  currentUsageCount: number;
  validFrom?: string;
  validUntil?: string;
  isActive: boolean;
  createdBy?: string;
  creator?: User;
  createdAt: string;
  updatedAt: string;
}

export interface DiscountValidationResult {
  code: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
  description?: string;
}

export interface DiscountCodeUsage {
  id: string;
  discountCodeId: string;
  userId?: string;
  guestEmail?: string;
  productType: 'course' | 'workshop' | 'competition';
  productId: string;
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
  paymentId?: string;
  status: 'pending' | 'applied' | 'refunded';
  user?: User;
  createdAt: string;
  updatedAt: string;
}

// Email Campaign types
export interface EmailCampaign {
  id: string;
  subject: string;
  body: string;
  htmlContent?: string;
  audienceType: string;
  audienceId?: string;
  audienceLabel: string;
  totalRecipients: number;
  sentCount: number;
  deliveredCount: number;
  failedCount: number;
  bouncedCount: number;
  status: 'draft' | 'sending' | 'sent' | 'failed';
  sentAt?: string;
  sentBy?: string;
  sender?: { id: string; firstName: string; lastName: string; email?: string };
  recipients?: EmailCampaignRecipient[];
  createdAt: string;
  updatedAt: string;
}

export interface EmailCampaignRecipient {
  id: string;
  campaignId: string;
  email: string;
  resendEmailId?: string;
  status: 'pending' | 'sent' | 'delivered' | 'bounced' | 'complained' | 'failed';
  errorMessage?: string;
  lastEvent?: string;
  statusCheckedAt?: string;
  createdAt: string;
}

export interface EmailCampaignAudience {
  type: string;
  id: string | null;
  label: string;
  count: number;
}

// Create singleton instance
const apiService = new ApiService();

export default apiService;